import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Upload, Download, Filter, X, Building2, MapPin } from 'lucide-react';
import { 
  Box, 
  Card, 
  CardContent, 
  Button, 
  Typography, 
  Grid, 
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Container,
  Stack
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { parseMonth, detectMonths, parseDateString, filterDataByMonth } from '../utils/dateUtils';
import {
  computeLHRDerivedFields,
  generateLHRTables,
  generateNotesPendingCityTable,
  filterNotesPendingLeads,
  mapNotesPendingLeadsForModal,
  NOTES_PENDING_MODAL_COLUMNS,
} from '../utils/lhrUtils';
import MultiSelect from './MultiSelect';
import MonthPills from './MonthPills';
import DataTable from './DataTable';
import ERPImport from './ERPImport';
import LeadDetailsModal from './LeadDetailsModal';

// Styled components
const UploadCard = styled(Card)(({ theme }) => ({
  border: '2px dashed #a5b4fc',
  backgroundColor: 'white',
  boxShadow: theme.shadows[2],
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[8],
  },
}));

const UploadButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(to right, #6366f1, #9333ea)',
  color: 'white',
  padding: '12px 24px',
  '&:hover': {
    background: 'linear-gradient(to right, #4f46e5, #7e22ce)',
    boxShadow: theme.shadows[4],
  },
}));

const FilterCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'white',
  boxShadow: theme.shadows[2],
}));

const GroupToggle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  backgroundColor: '#f1f5f9',
  borderRadius: '8px',
  padding: '4px',
}));

const GroupButton = styled(Button)(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: '8px 16px',
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: 500,
  textTransform: 'none',
  backgroundColor: active ? 'white' : 'transparent',
  color: active ? '#4f46e5' : '#475569',
  boxShadow: active ? theme.shadows[1] : 'none',
  '&:hover': {
    backgroundColor: active ? 'white' : '#e2e8f0',
    color: active ? '#4f46e5' : '#1e293b',
  },
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(20, 0),
}));

const EmptyStateCard = styled(Paper)(({ theme }) => ({
  display: 'inline-block',
  padding: theme.spacing(6),
  backgroundColor: 'white',
  borderRadius: '16px',
  boxShadow: theme.shadows[4],
}));

const LHRTab = () => {
  const [rawData, setRawData] = useState([]);
  const [derivedData, setDerivedData] = useState([]);
  const [tables, setTables] = useState(null);
  const [months, setMonths] = useState({ CM: null, CM1: null, CM2: null });
  const [selectedECs, setSelectedECs] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [ecOptions, setEcOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [fileName, setFileName] = useState('');
  const [groupBy, setGroupBy] = useState('city');
  const [overrideMonth, setOverrideMonth] = useState(null);
  const [notesPendingRaw, setNotesPendingRaw] = useState(null);
  const [notesPendingTable, setNotesPendingTable] = useState(null);
  const [notesPendingMeta, setNotesPendingMeta] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalLeads, setModalLeads] = useState([]);
  const [modalColumns, setModalColumns] = useState(null);

  useEffect(() => {
    if (rawData.length > 0) {
      processData(rawData);
    } else {
      setTables(null);
      setDerivedData([]);
    }
  }, [rawData, selectedECs, selectedCities, groupBy, overrideMonth]);

  useEffect(() => {
    if (!notesPendingRaw) {
      setNotesPendingTable(null);
      return;
    }

    let dataForTable = notesPendingRaw;
    const leads = Array.isArray(notesPendingRaw.leads) ? notesPendingRaw.leads : [];
    if (leads.length > 0 && (selectedECs.length > 0 || selectedCities.length > 0)) {
      const filteredLeads = leads.filter((lead) => {
        const ec = lead.experienceCenter || lead['Experience Center'] || '';
        const city = lead.city || lead.City || '';
        if (selectedECs.length > 0 && !selectedECs.includes(ec)) return false;
        if (selectedCities.length > 0 && !selectedCities.includes(city)) return false;
        return true;
      });
      dataForTable = {
        ...notesPendingRaw,
        leads: filteredLeads,
        _aggregatedFromLeads: true,
      };
    }

    setNotesPendingTable(generateNotesPendingCityTable(dataForTable, groupBy));
  }, [notesPendingRaw, groupBy, selectedECs, selectedCities]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { 
          raw: true,
          dateNF: 'dd/mm/yyyy'
        });
        
        const processedData = jsonData.map(row => {
          const newRow = { ...row };
          if (row['Lead Assigned Date'] && typeof row['Lead Assigned Date'] === 'number') {
            const excelEpoch = new Date(1899, 11, 30);
            const date = new Date(excelEpoch.getTime() + row['Lead Assigned Date'] * 86400000);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            newRow['Lead Assigned Date'] = `${day}/${month}/${year}`;
          }
          return newRow;
        });
        
        setRawData(processedData);
      } catch (error) {
        alert('Error reading file: ' + error.message);
      }
    };
    reader.readAsBinaryString(file);
  };

  const processData = (data) => {
    if (!data || data.length === 0) return;

    let detectedMonths;
    if (overrideMonth) {
      const [y, m] = overrideMonth.split('-').map(Number);
      const cm = overrideMonth;
      const cm1m = m - 1 === 0 ? 12 : m - 1;
      const cm1y = m - 1 === 0 ? y - 1 : y;
      const cm1 = `${cm1y}-${String(cm1m).padStart(2, '0')}`;
      const cm2m = cm1m - 1 === 0 ? 12 : cm1m - 1;
      const cm2y = cm1m - 1 === 0 ? cm1y - 1 : cm1y;
      const cm2 = `${cm2y}-${String(cm2m).padStart(2, '0')}`;
      detectedMonths = { CM: cm, CM1: cm1, CM2: cm2 };
    } else {
      detectedMonths = detectMonths(data, 'Lead Assigned Date');
    }
    setMonths(detectedMonths);

    const ecs = [...new Set(data.map(d => d['Experience Center']).filter(Boolean))];
    const cities = [...new Set(data.map(d => d.City).filter(Boolean))];
    setEcOptions(ecs);
    setCityOptions(cities);

    let filtered = data;
    if (selectedECs.length > 0) {
      filtered = filtered.filter(d => selectedECs.includes(d['Experience Center']));
    }
    if (selectedCities.length > 0) {
      filtered = filtered.filter(d => selectedCities.includes(d.City));
    }

    const withDerived = computeLHRDerivedFields(filtered, detectedMonths);
    setDerivedData(withDerived);

    const generatedTables = generateLHRTables(withDerived, detectedMonths, groupBy);
    setTables(generatedTables);
  };

  const handleNotesPendingCellClick = (clickInfo) => {
    const { groupColumn, groupValue, column } = clickInfo;
    if (!notesPendingRaw || groupValue === 'TOTAL') return;

    const groupByMode = groupColumn === 'EC Name' ? 'ec' : 'city';
    let leadsForFilter = notesPendingRaw;
    const allLeads = Array.isArray(notesPendingRaw.leads) ? notesPendingRaw.leads : [];
    if (selectedECs.length > 0 || selectedCities.length > 0) {
      const filteredLeads = allLeads.filter((lead) => {
        const ec = lead.experienceCenter || '';
        const city = lead.city || '';
        if (selectedECs.length > 0 && !selectedECs.includes(ec)) return false;
        if (selectedCities.length > 0 && !selectedCities.includes(city)) return false;
        return true;
      });
      leadsForFilter = { ...notesPendingRaw, leads: filteredLeads };
    }

    const filtered = filterNotesPendingLeads(leadsForFilter, groupByMode, groupValue, column);

    setModalTitle(`${groupValue} — ${column} (Notes Pending)`);
    setModalLeads(mapNotesPendingLeadsForModal(filtered));
    setModalColumns(NOTES_PENDING_MODAL_COLUMNS);
    setModalOpen(true);
  };

  const handleCellClick = (clickInfo) => {
    setModalColumns(null);
    const { tableTitle, groupColumn, groupValue, column } = clickInfo;
    
    let filteredLeads = derivedData.filter(lead => {
      const groupField = groupColumn === 'EC Name' ? 'Experience Center' : 'City';
      if (lead[groupField] !== groupValue) return false;
      
      const colLower = column.toLowerCase();
      let monthKey = null;
      if (colLower.includes('cm-2') || colLower.includes('month - 2') || colLower.includes('month-2')) {
        monthKey = months.CM2;
      } else if (colLower.includes('cm-1') || colLower.includes('month - 1') || colLower.includes('month-1')) {
        monthKey = months.CM1;
      } else if (colLower.includes('(cm)') || colLower.includes('month (cm)')) {
        monthKey = months.CM;
      }
      
      if (colLower.includes('lead assigned')) {
        return monthKey ? lead.monthKey === monthKey : true;
      }
      
      if (colLower.includes('closure') && !colLower.includes('additional') && !colLower.includes('meeting')) {
        const isClosureStage = lead['LHR Current Stage'] && lead['LHR Current Stage'].includes('SalesWon');
        return monthKey ? (lead.monthKey === monthKey && isClosureStage) : isClosureStage;
      }
      
      if (colLower.includes('right side')) {
        const rightStages = ['5-QS', '6-S/O Visit+Nego', '7-Hot Deal'];
        const isRight = rightStages.includes(lead['LHR Current Stage']);
        return monthKey ? (lead.monthKey === monthKey && isRight) : isRight;
      }
      
      if (colLower.includes('left side')) {
        const leftStages = ['2-LR', '3-RNC', '4-RC'];
        const isLeft = leftStages.includes(lead['LHR Current Stage']);
        return monthKey ? (lead.monthKey === monthKey && isLeft) : isLeft;
      }
      
      if (colLower.includes('lost')) {
        const isLost = lead['LHR Current Stage'] === '8-Lost';
        return monthKey ? (lead.monthKey === monthKey && isLost) : isLost;
      }
      
      if (colLower.includes('meeting done') && !colLower.includes('closure') && !colLower.includes('active') && !colLower.includes('additional')) {
        const hasMeeting = lead['LHR Meeting Done'] === 'Yes';
        return monthKey ? (lead.monthKey === monthKey && hasMeeting) : hasMeeting;
      }
      
      if (colLower.includes('closures from meeting')) {
        const hasMeeting = lead['LHR Meeting Done'] === 'Yes';
        const isClosure = lead['LHR Stage Post Meeting'] && lead['LHR Stage Post Meeting'].includes('SalesWon');
        return monthKey ? (lead.monthKey === monthKey && hasMeeting && isClosure) : (hasMeeting && isClosure);
      }
      
      if (colLower.includes('active leads from meeting')) {
        const hasMeeting = lead['LHR Meeting Done'] === 'Yes';
        const isActive = lead['LHR Stage Post Meeting'] === '3-Active';
        return monthKey ? (lead.monthKey === monthKey && hasMeeting && isActive) : (hasMeeting && isActive);
      }
      
      if (colLower.includes('quotation shared')) {
        const hasQuotation = lead['LHR Quotation Sent'] === 'Yes';
        return monthKey ? (lead.monthKey === monthKey && hasQuotation) : hasQuotation;
      }
      
      if (colLower.includes('lead received criteria')) {
        return lead['LHR Current Stage'] === '2-LR' && lead['LHR Dates'] >= 1;
      }
      if (colLower.includes('rnc criteria')) {
        return lead['LHR Current Stage'] === '3-RNC' && lead['LHR Dates'] >= 5;
      }
      if (colLower.includes('rc criteria')) {
        return lead['LHR Current Stage'] === '4-RC' && lead['LHR Dates'] >= 2;
      }
      if (colLower.includes('hdcl not filled')) {
        return lead['LHR Current Stage'] === '5-QS' && (lead['Cx Move-in Timeline'] === 'NA' || lead['Cx Move-in Timeline']?.toString().toUpperCase() === 'NA');
      }

      if (colLower.includes('meeting not fixed')) {
        if (lead['Lead Type'] !== 'Real') return false;
        if (lead['Current Stage'] !== 'Quotation Sent') return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const meetingDateStr = lead['updateMeetingDetails'];

        const isEmpty = !meetingDateStr || meetingDateStr === '-' || meetingDateStr === '';
        if (isEmpty) return true;

        const meetingDate = parseDateString(meetingDateStr);
        return (
          meetingDate instanceof Date &&
          !isNaN(meetingDate) &&
          meetingDate < today
        );
      }
      
      return monthKey ? lead.monthKey === monthKey : true;
    });
    
    setModalTitle(`${groupValue} - ${column} (${filteredLeads.length} leads)`);
    setModalLeads(filteredLeads);
    setModalOpen(true);
  };

  const handleExport = () => {
    if (!derivedData || derivedData.length === 0) {
      alert('No data to export');
      return;
    }

    const wb = XLSX.utils.book_new();
    const derivedSheet = XLSX.utils.json_to_sheet(derivedData);
    XLSX.utils.book_append_sheet(wb, derivedSheet, 'Derived Data');

    if (tables) {
      ['table1', 'table2', 'table3', 'table4', 'table5'].forEach((key, idx) => {
        if (tables[key]) {
          const sheet = XLSX.utils.json_to_sheet(tables[key]);
          XLSX.utils.book_append_sheet(wb, sheet, `Table${idx + 1}`);
        }
      });
    }

    XLSX.writeFile(wb, `LHR_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {/* Upload Section */}
      {/* <UploadCard>
        <CardContent sx={{ p: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
              <UploadButton
                component="label"
                variant="contained"
                startIcon={<Upload size={20} />}
                sx={{ cursor: 'pointer' }}
              >
                Upload Lead Export (.xlsx)
                <input
                  type="file"
                  accept=".xlsx,.csv"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </UploadButton>
              {fileName && (
                <Typography variant="body2" sx={{ mt: 2, color: '#475569' }}>
                  Loaded: <Box component="span" sx={{ color: '#4f46e5', fontWeight: 500 }}>{fileName}</Box>
                </Typography>
              )}
            </Box>
            {rawData.length > 0 && (
              <Button
                onClick={handleExport}
                variant="contained"
                startIcon={<Download size={16} />}
                sx={{ 
                  bgcolor: '#10b981', 
                  '&:hover': { bgcolor: '#059669' },
                  boxShadow: 2,
                }}
              >
                Export Excel
              </Button>
            )}
          </Box>
        </CardContent>
      </UploadCard> */}

      {/* ERP Import Section */}
      {/* {rawData.length === 0 && ( */}
        <ERPImport onDataImport={(data, selectedMonth, notesPending) => {
          setOverrideMonth(selectedMonth || null);
          setRawData(Array.isArray(data) ? data : []);
          setNotesPendingRaw(notesPending || null);
          setNotesPendingMeta(
            notesPending
              ? {
                  pendingCount: notesPending.pendingCount,
                  monthWindowLabel: notesPending.monthWindowLabel,
                  selected_month: notesPending.selected_month,
                }
              : null
          );

          const ecs = new Set();
          const cities = new Set();
          (Array.isArray(data) ? data : []).forEach((row) => {
            if (row['Experience Center']) ecs.add(row['Experience Center']);
            if (row.City) cities.add(row.City);
          });
          (notesPending?.leads || []).forEach((lead) => {
            if (lead.experienceCenter) ecs.add(lead.experienceCenter);
            if (lead.city) cities.add(lead.city);
          });
          setEcOptions([...ecs].sort());
          setCityOptions([...cities].sort());
          setFileName('ERP Import');
        }} />
      {/* )} */}

      {(rawData.length > 0 || (notesPendingTable && notesPendingTable.length > 0)) && (
        <>
          {/* Filters */}
          {(rawData.length > 0 || notesPendingRaw?.leads?.length > 0) && (
          <FilterCard>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Filter size={20} style={{ color: '#4f46e5' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Filters
                  </Typography>
                </Box>
                
                {/* Group By Toggle */}
                <GroupToggle>
                  <GroupButton
                    onClick={() => setGroupBy('city')}
                    active={groupBy === 'city'}
                    data-testid="group-by-city"
                  >
                    <MapPin size={16} />
                    City
                  </GroupButton>
                  <GroupButton
                    onClick={() => setGroupBy('ec')}
                    active={groupBy === 'ec'}
                    data-testid="group-by-ec"
                  >
                    <Building2 size={16} />
                    Experience Center
                  </GroupButton>
                </GroupToggle>
              </Box>
              
              {/* MultiSelect in one line - Simple flexbox with proper width */}
              <Box sx={{ 
                display: 'flex', 
                gap: 3,
                width: '100%',
                '& > *': {
                  flex: 1,
                  minWidth: 0, // Prevents overflow
                }
              }}>
                <MultiSelect
                  label="Experience Center"
                  options={ecOptions}
                  selected={selectedECs}
                  onChange={setSelectedECs}
                  testId="lhr-ec-filter"
                />
                <MultiSelect
                  label="City"
                  options={cityOptions}
                  selected={selectedCities}
                  onChange={setSelectedCities}
                  testId="lhr-city-filter"
                />
              </Box>
            </CardContent>
          </FilterCard>
          )}

          {rawData.length > 0 && <MonthPills months={months} />}

          {(tables || (notesPendingTable && notesPendingTable.length > 0)) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {tables && (
                <>
                  <DataTable
                    title="Table 1: Lead Pipeline"
                    data={tables.table1}
                    months={months}
                    testId="lhr-table1"
                    onCellClick={handleCellClick}
                  />
                  <DataTable
                    title="Table 2: Leads where tasks are not done properly"
                    data={tables.table5}
                    months={months}
                    testId="lhr-table5"
                    onCellClick={handleCellClick}
                  />
                </>
              )}
              {notesPendingTable && notesPendingTable.length > 0 && (
                <DataTable
                  title={
                    ` Table 3 : Leads where no updates in last 1 day`
                  }
                  data={notesPendingTable}
                  months={months}
                  testId="lhr-notes-pending-table"
                  onCellClick={handleNotesPendingCellClick}
                />
              )}
              {tables && (
                <>
                  <DataTable
                    title="Table 4: Lead 2 Meeting and Meeting 2 Closure"
                    data={tables.table3}
                    months={months}
                    testId="lhr-table3"
                    onCellClick={handleCellClick}
                  />
                  <DataTable
                    title="Table 5: Lead to Quotation and Quotation to Closure"
                    data={tables.table4}
                    months={months}
                    testId="lhr-table4"
                    onCellClick={handleCellClick}
                  />
                  <DataTable
                    title="Table 6: Lead 2 Closure and Additional Closure"
                    data={tables.table2}
                    months={months}
                    testId="lhr-table2"
                    onCellClick={handleCellClick}
                  />
                </>
              )}
            </Box>
          )}
        </>
      )}

      <LeadDetailsModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalColumns(null);
        }}
        title={modalTitle}
        leads={modalLeads}
        columns={modalColumns}
      />
    </Box>
  );
};

export default LHRTab;